import { BulletList, AlertBox, Screenshot, ScreenshotGrid } from '../components/UIComponents';
import { Package, BarChart3, FileText, Link2, ShieldCheck } from 'lucide-react';

export function PatchManagement() {
    return (
        <section className="doc-section scroll-mt-20 mt-12" id="patch-management">
            <div className="mb-3 pb-2 border-b border-white/10">
                <h2 className="text-xl font-bold flex items-center gap-2.5 text-gray-100">
                    <Package className="text-emerald-500" size={22} />
                    16. Patch Management & Update Deployment
                </h2>
                <p className="text-[14px] text-gray-200 mt-1.5 leading-relaxed">
                    A built-in tool that helps you schedule, deploy, and track operating system and application updates across your Windows and Linux machines.
                </p>
            </div>
            <div className="bg-gray-800 border border-white/5 rounded-xl p-5">
                <h3 className="text-[14px] font-bold text-gray-100 mb-3">16.1 Patch Management Capabilities</h3>
                <BulletList items={[
                    'OS Patching: Push Windows Updates and Linux (apt/yum) package updates directly.',
                    'Third-Party Apps: Keep browsers and common office software up to date.',
                    'Maintenance Windows: Pick a time to install updates so you don\'t interrupt users.',
                    'Rollbacks: Easily undo a patch if it breaks something.',
                ]} />
                <h3 className="text-[14px] font-bold text-gray-100 mt-5 mb-3">16.2 Patch Tracking & Visibility</h3>
                <BulletList items={[
                    'Tracking: See exactly which computers need updates, which failed, and which need a reboot.',
                    'Jobs: Keep an eye on deployment progress, see success rates, and check logs in one view.',
                    'Reports: Schedule patch status reports to keep your team informed.'
                ]} />
            </div>
            <ScreenshotGrid>
                <Screenshot
                    src="/Patch Management & Update Deployment.png"
                    alt="Patch Management & Update Deployment"
                />
                <Screenshot
                    src="/Patch Management & Update Deployment1.png"
                    alt="Patch Management & Update Deployment"
                />
            </ScreenshotGrid>
        </section>
    );
}

export function ReportingAnalytics() {
    return (
        <section className="doc-section scroll-mt-20 mt-12" id="reporting-analytics">
            <div className="mb-3 pb-2 border-b border-white/10">
                <h2 className="text-xl font-bold flex items-center gap-2.5 text-gray-100">
                    <BarChart3 className="text-blue-300" size={22} />
                    17. Reporting & Analytics
                </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-gray-800 border border-white/5 rounded-xl p-5">
                    <h3 className="text-[14px] font-bold text-gray-100 mb-3">17.1 Standard Reports (13)</h3>
                    <div className="grid grid-cols-1 gap-1 text-[12px] text-gray-300">
                        {['Network Device Inventory', 'Switch Port Utilization', 'Camera Status & Health', 'Server Health & Resource', 'iLO / iDRAC Hardware Status', 'Firewall Compliance Audit', 'Patch Status & Compliance', 'Event & Incident Summary', 'Asset Uptime', 'Active Directory Audit', 'SSL Certificate Expiry', 'User Activity & Audit Log', 'Custom Asset Report'].map(r => (
                            <div key={r} className="flex items-center gap-2">• {r}</div>
                        ))}
                    </div>
                </div>
                <div className="bg-gray-800 border border-white/5 rounded-xl p-5">
                    <h3 className="text-[14px] font-bold text-gray-100 mb-3">17.2 Report Features</h3>
                    <BulletList items={[
                        'Export as PDF or CSV files.',
                        'Send reports automatically via email.',
                        'Download individual PDF summaries for specific assets.',
                        'Build your own reports using custom filters and groups.',
                        'Track uptime, patching, and compliance history over time.'
                    ]} />
                </div>
            </div>
            <ScreenshotGrid>
                <Screenshot
                    src="/Reporting_&_Analytics.png"
                    alt="Reporting & Analytics"
                />
                <Screenshot
                    src="/Reporting_&_Analytics1.png"
                    alt="Reporting & Analytics"
                />
            </ScreenshotGrid>
        </section>
    );
}

export function AuditLogs() {
    return (
        <section className="doc-section scroll-mt-20 mt-12" id="audit-logs">
            <div className="mb-3 pb-2 border-b border-white/10">
                <h2 className="text-xl font-bold flex items-center gap-2.5 text-gray-100">
                    <FileText className="text-gray-400" size={22} />
                    18. Audit Logs & Compliance Tracking
                </h2>
            </div>
            <div className="bg-gray-800 border border-white/5 rounded-xl p-5">
                <p className="text-[14px] text-gray-200 leading-relaxed mb-4">
                    Every action taken in the platform is securely recorded so you always know who made what changes.
                </p>
                <h3 className="text-[14px] font-bold text-gray-100 mb-3">18.1 Audit Log Record & 18.2 Compliance Alignment</h3>
                <AlertBox type="note" icon={<ShieldCheck size={16} />}>
                    Logs capture User, Tenant Context, Action Taken, Description, Status, Source IP, object affected, before/after values where applicable, and exact UTC timestamp.
                </AlertBox>
                <p className="text-[13px] text-gray-200 leading-relaxed mt-4">
                    These logs help you meet compliance requirements by giving you clear proof of what happened, who did it, and when it occurred.
                </p>
            </div>
            <Screenshot
                src="/Audit_Logs_&_Compliance_Tracking.png"
                alt="Audit Logs & Compliance Tracking"
            />
        </section>
    );
}

export function Integrations() {
    return (
        <section className="doc-section scroll-mt-20 mt-12" id="integrations">
            <div className="mb-3 pb-2 border-b border-white/10">
                <h2 className="text-xl font-bold flex items-center gap-2.5 text-gray-100">
                    <Link2 className="text-indigo-500" size={22} />
                    19. Third-Party Integrations
                </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div className="p-4 bg-gray-900 border border-white/5 rounded-xl">
                    <h4 className="text-[13px] font-bold text-indigo-400 mb-2">19.1 Identity & SSO</h4>
                    <p className="text-[11px] text-gray-300">Azure AD, Google Workspace, GitHub OAuth, SAML 2.0, OpenID Connect.</p>
                </div>
                <div className="p-4 bg-gray-900 border border-white/5 rounded-xl">
                    <h4 className="text-[13px] font-bold text-indigo-400 mb-2">19.2 Notification & Collaboration</h4>
                    <p className="text-[11px] text-gray-300">Microsoft Teams, Slack, Email (SMTP), WhatsApp Business API.</p>
                </div>
                <div className="p-4 bg-gray-900 border border-white/5 rounded-xl">
                    <h4 className="text-[13px] font-bold text-indigo-400 mb-2">19.3 Ticketing & Service</h4>
                    <p className="text-[11px] text-gray-300">RunDesk Pro with ticket creation and status sync, plus generic webhook integration for custom ticketing or SOAR platforms.</p>
                </div>
                <div className="p-4 bg-gray-900 border border-white/5 rounded-xl">
                    <h4 className="text-[13px] font-bold text-indigo-400 mb-2">19.4 API & Developer</h4>
                    <p className="text-[11px] text-gray-300">REST API CRUD, webhook events for creation and closure, and per-tenant dashboard-configurable integration settings.</p>
                </div>
            </div>
            <Screenshot
                src="/Third_Party_Integrations.png"
                alt="Third Party Integrations"
            />
        </section>
    );
}

export function CustomDashboards() {
    return (
        <section className="doc-section scroll-mt-20 mt-12" id="custom-dashboards">
            <div className="mb-3 pb-2 border-b border-white/10">
                <h2 className="text-xl font-bold flex items-center gap-2.5 text-gray-100">
                    <BarChart3 className="text-cyan-400" size={22} />
                    17.5 Custom Dashboards
                </h2>
                <p className="text-[14px] text-gray-200 mt-1.5 leading-relaxed">
                    Build your own personalized views by combining widgets and data sources that matter most to your team's unique workflows.
                </p>
            </div>
            <div className="bg-gray-800 border border-white/5 rounded-xl p-5">
                <h3 className="text-[14px] font-bold text-gray-100 mb-3">17.5.1 Dashboard Builder Capabilities</h3>
                <BulletList items={[
                    'Drag & Drop Interface: Easily arrange, resize, and configure widgets to create the perfect layout.',
                    'Data Sources: Combine metrics from different systems (SNMP, Agents, APIs, Kubernetes, etc.) into a single pane of glass.',
                    'Granular Filtering: Scope individual widgets or entire dashboards by asset tags, sites, subnets, or specific time ranges.',
                    'Sharing & Access: Share your custom dashboards with specific users, roles, or across your entire tenant securely.'
                ]} />
            </div>
            <ScreenshotGrid>
                <Screenshot
                    src="/Custom_Dashboard_Builder.png"
                    alt="Custom Dashboard Builder"
                />
                <Screenshot
                    src="/Custom_Dashboard_View.png"
                    alt="Custom Dashboard View"
                />
            </ScreenshotGrid>
        </section>
    );
}
